import {Controller} from "@hotwired/stimulus"

// Sidebar report-argument filters (Linear-style left sidebar).
// One controller per <details> — a lazy-loaded reference-argument group: options
// are fetched from argument_search on first expand (capped server-side via
// &limit=), per-group search re-queries with &q=.
export default class extends Controller {
  static targets = ["list", "search", "count", "status", "rowTemplate"]
  static values = {
    url: String,
    name: String,
    index: Number,
    multiple: {type: Boolean, default: true},
    dependent: {type: Boolean, default: false},
    limit: {type: Number, default: 250}
  }

  // server-rendered <details open> doesn't emit a toggle event, so load here
  connect() {
    // data-turbo-permanent moves this DOM across visits — a fresh instance must not refetch an already-loaded list
    this.loaded = !!this.listTarget.querySelector('[data-fetched]')
    this.opened()
  }

  disconnect() {
    clearTimeout(this.searchTimer)
  }

  // <details> toggle — first expand triggers the fetch
  opened() {
    if (this.element.open && !this.loaded) this.load("")
  }

  search() {
    clearTimeout(this.searchTimer)
    const q = this.searchTarget.value.trim()
    this.searchTimer = setTimeout(() => this.load(q), 300)
  }

  load(q) {
    this.loaded = true
    const seq = (this.seq = (this.seq || 0) + 1)
    const url = new URL(this.urlValue, window.location.origin)
    if (q) url.searchParams.set("q", q)
    else url.searchParams.delete("q")
    this.showStatus("Loading…")
    fetch(url, {headers: {Accept: "application/json"}})
      .then(response => response.json())
      .then(json => {
        if (seq === this.seq) this.renderRows(json.items || [], q)
      })
      .catch(() => {
        if (seq === this.seq) this.showStatus("Failed to load")
      })
  }

  renderRows(items, q) {
    // drop rows from the previous fetch unless the user has them checked —
    // keeps the DOM bounded at ~limit rows + selections
    this.listTarget.querySelectorAll('[data-fetched="true"]').forEach(row => {
      const input = row.querySelector("input")
      if (!input || !input.checked) row.remove()
    })

    const existing = {}
    this.listTarget.querySelectorAll("input").forEach(input => (existing[input.value] = input))

    const fragment = document.createDocumentFragment()
    items.slice(0, this.limitValue).forEach(item => {
      const id = String(item.id)
      const text = String(item.text ?? item.id)
      if (existing[id]) {
        // server-rendered checked rows show the raw id — upgrade to display text
        const span = existing[id].closest("label")?.querySelector("span")
        if (span) span.textContent = text
        return
      }
      const row = this.rowTemplateTarget.content.firstElementChild.cloneNode(true)
      row.dataset.fetched = "true"
      const input = row.querySelector("input")
      input.type = this.multipleValue ? "checkbox" : "radio"
      input.name = this.multipleValue ? `${this.nameValue}[]` : this.nameValue
      input.value = id
      row.querySelector("span").textContent = text
      fragment.appendChild(row)
    })
    this.listTarget.appendChild(fragment)

    if (items.length === 0) {
      this.showStatus(q ? "No matches" : "No options")
    } else if (items.length >= this.limitValue) {
      this.showStatus(`Showing first ${this.limitValue} — type to refine`)
    } else {
      this.hideStatus()
    }
  }

  // group role: an option was (un)checked — refresh the badge and, when this
  // argument drives dependent filters, tell later groups to rebuild their URLs
  changed() {
    const values = Array.from(this.listTarget.querySelectorAll("input:checked"))
      .map(input => input.value)
      .filter(value => value !== "")
    if (this.hasCountTarget) {
      this.countTarget.textContent = values.length
      this.countTarget.classList.toggle("hidden", values.length === 0)
    }
    if (this.dependentValue) {
      window.dispatchEvent(new CustomEvent("sidebar-filter:arg-changed", {
        detail: {name: this.nameValue, values: values, index: this.indexValue, multiple: this.multipleValue}
      }))
    }
  }

  // group role: an earlier argument changed — rewrite our fetch URL with its
  // new value(s) and refetch if we're open (mirrors select_controller cascade)
  siblingChanged(event) {
    if (!this.hasListTarget) return
    const {name, values, index, multiple} = event.detail
    if (name === this.nameValue || index >= this.indexValue) return
    const url = new URL(this.urlValue, window.location.origin)
    url.searchParams.delete(`${name}[]`)
    url.searchParams.delete(name)
    if (multiple) values.forEach(value => url.searchParams.append(`${name}[]`, value))
    else if (values[0]) url.searchParams.set(name, values[0])
    this.urlValue = url.toString()
    this.loaded = false
    if (this.element.open) this.load(this.hasSearchTarget ? this.searchTarget.value.trim() : "")
  }

  showStatus(message) {
    if (!this.hasStatusTarget) return
    this.statusTarget.textContent = message
    this.statusTarget.classList.remove("hidden")
  }

  hideStatus() {
    if (this.hasStatusTarget) this.statusTarget.classList.add("hidden")
  }
}
